import { Feature, Map, MapBrowserEvent } from "ol";
import Collection from "ol/Collection";
import { Geometry, Point } from "ol/geom";
import VectorLayer from "ol/layer/Vector";
import { fromLonLat } from "ol/proj";
import { Stroke, Fill, Icon, Style } from "ol/style";
import CircleStyle from "ol/style/Circle";
import VectorSource from "ol/source/Vector";
import { TooltipManager } from "./tooltip";
import { geojson } from "@lib/geojson";
import Modify, { ModifyEvent } from "ol/interaction/Modify";
import { easeOut } from "ol/easing";

export interface MarkerPoint {
    coordinate: geojson.Coord,
    coordinateSystem: 'utm' | 'wgs84',
    content: string,
    StyleType: MarkerStyleType,
    category: MarkerCategory,
    color?: string,
    icon?: string,
    draggable?: boolean,
    dropCallback?: (geometry: geojson.GeometryPoint) => Promise<void>,
}

export interface FeatureTooltip<T extends Geometry> {
    feature: Feature<T>,
    content: string,
    coordinate: geojson.Coord,
    category: 'individual' | 'group',
    type: string,
}

export type MarkerStyleType = 'circle' | 'circle-icon';
export type MarkerCategory = 'device' | 'vehicle' | 'none';

export class Marker {
    private readonly mapElement: Map;
    private readonly tooltip: TooltipManager;
    private vectorSource = new VectorSource();
    private vectorLayer = new VectorLayer({ source: this.vectorSource, zIndex: 1000 });
    public readonly items = new Set<MarkerPoint>();
    public features = new Set<FeatureTooltip<Point>>();
    private liveMovableMarkers: { [id: string]: Feature<Point> } = {};
    private markerFeatures = new Set<Feature<Point>>();
    private readonly draggableFeatures = new Collection<Feature<Point>>([], { unique: true });
    private readonly modifyInteraction: Modify;

    constructor(mapElement: Map, tooltip: TooltipManager) {
        this.mapElement = mapElement;
        this.tooltip = tooltip;
        this.mapElement.addLayer(this.vectorLayer);
        this.modifyInteraction = new Modify({
            features: this.draggableFeatures,
            hitDetection: this.vectorLayer,
        });
        this.mapElement.addInteraction(this.modifyInteraction);
        this.modifyInteraction.on('modifyend', this.handleModifyEnd.bind(this));
        this.mapElement.on('pointermove', this.handlePointerMove.bind(this));
    }

    add(item: MarkerPoint) {
        this.items.add(item);
        const mapCoordinate = this.toMapCoordinate(item.coordinate, item.coordinateSystem);
        const markerFeature = new Feature({
            geometry: new Point(mapCoordinate),
            name: item.content,
        });

        const featureTooltip: FeatureTooltip<Point> = {
            feature: markerFeature,
            content: item.content,
            coordinate: mapCoordinate,
            category: 'group',
            type: item.category,
        };

        this.features.add(featureTooltip);
        markerFeature.setStyle(this.getMarkerStyle(item));
        this.vectorSource.addFeature(markerFeature);

        if (item.draggable) {
            if (item.dropCallback) {
                markerFeature.set('dropCallback', item.dropCallback);
            }
            this.draggableFeatures.push(markerFeature);
        }
        this.markerFeatures.add(markerFeature);

        return markerFeature;
    }

    clearByCategory(category: MarkerCategory) {
        const featuresToRemove: Feature<Point>[] = [];
        const tooltipsToRemove: FeatureTooltip<Point>[] = [];

        this.features.forEach(tooltip => {
            if (tooltip.type === category) {
                featuresToRemove.push(tooltip.feature);
                tooltipsToRemove.push(tooltip);
            }
        });

        featuresToRemove.forEach(feature => {
            this.vectorSource.removeFeature(feature);
            this.markerFeatures.delete(feature);
            this.draggableFeatures.remove(feature);
        });

        tooltipsToRemove.forEach(tooltip => this.features.delete(tooltip));
        featuresToRemove.forEach(feature => this.tooltip.hideTooltip(feature));
    }

    public syncLiveMovableMarkers(propsList: { id: string; lastLat: number; lastLong: number; name: string, draggable?: boolean, color?: string }[]) {
        const visibleIds = new Set((propsList ?? []).map((props) => `${props.id}`));

        Object.entries(this.liveMovableMarkers).forEach(([id, feature]) => {
            if (visibleIds.has(id)) {
                return;
            }

            this.tooltip.hideTooltip(feature);
            this.vectorSource.removeFeature(feature);
            this.markerFeatures.delete(feature);
            this.draggableFeatures.remove(feature);

            const tooltip = [...this.features].find((item) => item.feature === feature);
            if (tooltip) {
                this.features.delete(tooltip);
            }

            delete this.liveMovableMarkers[id];
        });

        (propsList ?? []).forEach((props) => this.updateliveMovableMarker(props));
    }

    updateliveMovableMarker(props: { id: string; lastLat: number; lastLong: number; name: string, draggable?: boolean, color?: string }) {
        if (!props.lastLat || !props.lastLong) return;

        const newCoordinate = fromLonLat([props.lastLong, props.lastLat]) as geojson.Coord;
        const content = `<strong>Vehicle: </strong> ${props.name}`;

        if (this.liveMovableMarkers[props.id]) {
            const markerFeature = this.liveMovableMarkers[props.id];
            this.updateFeatureTooltip(markerFeature, { content });
            this.animateMarkerMovement(markerFeature, newCoordinate);
        } else {
            const markerFeature = this.add({
                coordinate: [props.lastLong, props.lastLat],
                coordinateSystem: 'wgs84',
                content,
                icon: '../../../assets/mat-icons/machine.svg',
                color: props.color || '#DF0C0C',
                category: 'vehicle',
                StyleType: 'circle-icon',
                draggable: props.draggable ?? false,
            });

            this.liveMovableMarkers[props.id] = markerFeature;
        }
    }

    private animateMarkerMovement(marker: Feature<Point>, newCoordinate: geojson.Coord) {
        const currentGeometry = marker.getGeometry() as Point;
        const currentCoords = currentGeometry.getCoordinates();
        const duration = 3000;

        const startTime = Date.now();
        const animate = () => {
            const elapsedTime = Date.now() - startTime;
            const fraction = Math.min(elapsedTime / duration, 1);
            const easingFactor = easeOut(fraction);

            const interpolatedCoords = [
                currentCoords[0] + (newCoordinate[0] - currentCoords[0]) * easingFactor,
                currentCoords[1] + (newCoordinate[1] - currentCoords[1]) * easingFactor,
            ];

            currentGeometry.setCoordinates(interpolatedCoords);
            this.updateFeatureTooltip(marker, { coordinate: interpolatedCoords as geojson.Coord });

            if (fraction < 1) {
                requestAnimationFrame(animate);
            }
        };

        requestAnimationFrame(animate);
    }

    private handleModifyEnd(event: ModifyEvent) {
        event.features.forEach((feature) => {
            const geometry = feature.getGeometry() as Point | undefined;
            const dropCallback = feature.get('dropCallback') as MarkerPoint['dropCallback'] | undefined;

            if (!geometry || !dropCallback) {
                return;
            }

            const coordinates = geometry.getCoordinates();
            const newGeometry: geojson.GeometryPoint = {
                type: 'Point',
                coordinates: [coordinates[0], coordinates[1]],
            };

            void dropCallback(newGeometry);
        });
    }

    private getMarkerStyle(point: MarkerPoint): Style[] {
        const styles: Style[] = [
            new Style({
                image: new CircleStyle({
                    radius: 15,
                    fill: new Fill({ color: point.color || '#5271FF' }),
                    stroke: new Stroke({ color: '#ffffff', width: 2 }),
                }),
            }),
        ];

        if (point.icon) {
            styles.push(
                new Style({
                    image: new Icon({ src: point.icon, scale: 0.3, anchor: [0.5, 0.5] }),
                    zIndex: 1,
                })
            );
        }
        return styles;
    }

    private handlePointerMove(event: MapBrowserEvent<any>) {
        const pixel = this.mapElement.getEventPixel(event.originalEvent);
        const featureAtPixel = this.mapElement.forEachFeatureAtPixel(pixel, feature => feature);

        if (featureAtPixel) {
            const matchedFeature = [...this.features].find(f => f.feature === featureAtPixel);
            if (matchedFeature) {
                this.tooltip.showTooltip([matchedFeature]);
                return;
            }
        }
        this.tooltip.hideAllTooltips('device');
        this.tooltip.hideAllTooltips('vehicle');
    }

    private toMapCoordinate(coordinate: geojson.Coord, coordinateSystem: MarkerPoint['coordinateSystem']): geojson.Coord {
        return coordinateSystem === 'wgs84' ? fromLonLat(coordinate) as geojson.Coord : coordinate;
    }

    private updateFeatureTooltip(
        feature: Feature<Point>,
        updates: { content?: string, coordinate?: geojson.Coord }
    ) {
        const featureTooltip = [...this.features].find((tooltip) => tooltip.feature === feature);
        if (!featureTooltip) {
            return;
        }

        if (updates.content !== undefined) {
            featureTooltip.content = updates.content;
        }

        if (updates.coordinate !== undefined) {
            featureTooltip.coordinate = updates.coordinate;
        }

        this.tooltip.updateTooltip(feature, featureTooltip.content, featureTooltip.coordinate);
    }
}
